import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Banner as BannerType } from '@/services/bannerService';
import { useBanner } from '@/context/BannerContext';
import { Button } from '@/components/ui/button';
import { Timestamp } from 'firebase/firestore';
import Countdown from './Countdown';

interface BannerProps {
  banner: BannerType;
  className?: string;
}

const Banner: React.FC<BannerProps> = ({ banner, className }) => {
  const { dismissBanner } = useBanner();
  
  // Check if countdown is enabled and has expired
  const isCountdownExpired = () => {
    if (!banner.countdown?.enabled || !banner.countdown.endDate) {
      return false;
    }
    
    let endDateTime: Date;
    if (banner.countdown.endDate instanceof Timestamp) {
      endDateTime = banner.countdown.endDate.toDate();
    } else {
      endDateTime = new Date(banner.countdown.endDate);
    }
    
    return endDateTime.getTime() <= new Date().getTime();
  };
  
  // Hide banner if countdown is expired
  if (isCountdownExpired()) {
    return null;
  }
  
  const backgroundColorClass = banner.backgroundColor === 'jamaica-green' 
    ? 'bg-jamaica-green' 
    : 'bg-jamaica-black';

  const handleDismiss = () => {
    dismissBanner(banner.id);
  };

  const Content = (
    <div
      className={cn(
        "w-full py-2 px-6 transition-all duration-500 relative",
        backgroundColorClass,
        className
      )}
    >
      <div className="max-w-7xl mx-auto relative">
        <div className="text-white text-xs sm:text-sm font-medium text-center leading-relaxed flex flex-col items-center gap-1">
          <span>{banner.text}</span>
          
          {/* Countdown Timer */}
          {banner.countdown?.enabled && banner.countdown.endDate && (
            <Countdown
              endDate={banner.countdown.endDate}
              format={banner.countdown.format}
              prefix={banner.countdown.prefix}
              suffix={banner.countdown.suffix}
              className="text-white"
            />
          )}
        </div>
      </div>
      
      {/* Close button - positioned relative to full banner width */}
      {banner.showCloseButton && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-white/20 text-white hover:text-white"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDismiss(); }}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close banner</span>
        </Button>
      )}
    </div>
  );

  // If link provided, wrap content to make banner clickable
  if (banner.link && banner.link.trim()) {
    const href = banner.link.trim();
    const isInternal = href.startsWith('/');
    return isInternal ? (
      <Link to={href} className="block">
        {Content}
      </Link>
    ) : (
      <a href={href} className="block" rel="noopener noreferrer">
        {Content}
      </a>
    );
  }

  return Content;
};

export default Banner; 
